<script lang="ts">
  /**
   * Die Bildwahl: alle Fotos der Reise nebeneinander, eines davon wird das
   * Titelbild.
   *
   * ## Warum es diesen Dialog gibt
   *
   * Die Wahl existiert seit dem 26.8.2026 — aber nur an jedem einzelnen Foto
   * im Tagebuch („Als Titelbild"). Wer das Bild seiner Reise ändern wollte,
   * suchte es dort, wo es steht: in der Reiseliste, an der Karte, auf der es
   * zu sehen ist. Ein Befund vom 27.8.2026, und er ist der übliche: Eine
   * Handlung gehört an ihren Gegenstand, nicht in die Fläche, aus der der
   * Gegenstand stammt.
   *
   * Der Dialog fügt der Sache **nichts** hinzu, was es nicht schon gäbe. Er
   * schreibt denselben Schlüssel (`settings.titelbild` über `mitTitelbild`),
   * und „Automatik" nimmt die Wahl genauso zurück wie
   * „Automatik wiederherstellen" am Foto. Er macht sie nur auffindbar — und
   * zeigt dabei zum ersten Mal alle Fotos nebeneinander, was die eigentliche
   * Hilfe ist: Man erkennt sein Bild, man erinnert sich nicht an seinen Tag.
   *
   * ## Der Satz über die ungeteilten Fotos
   *
   * Zur Wahl stehen **alle** Fotos, auch die aus zurückgehaltenen Einträgen —
   * es ist die eigene Reiseliste, und wer sein Lieblingsbild aus einem privaten
   * Eintrag nehmen will, darf das. Nur sieht die geteilte Seite es nie: Dort
   * fällt der Server auf die Automatik zurück (`oeffentlich.titelbild`), und
   * zwar hart, weil die Adresse dieser Seite weitergeleitet werden kann.
   *
   * Das steht deshalb an den betroffenen Kacheln und **einmal** als Satz
   * darüber — und der Satz nur, wenn es überhaupt ungeteilte Fotos gibt (siehe
   * `titelbildwahl.ungeteilte`). Eine Warnung vor nichts ist die zuverlässigste
   * Art, die nächste Warnung zu entwerten.
   *
   * ## Die Form kommt von der Kopier-Abfrage
   *
   * `role="dialog"` mit `aria-modal` am Rahmen, Escape führt hinaus, der
   * Abbrechen-Knopf trägt den Anfangsfokus und steht links; der Vorhang gehört
   * dem Aufrufer. Nicht übernommen ist das Formular: Hier gibt es nichts zu
   * bestätigen — ein Klick auf ein Bild **ist** die Entscheidung, genauso wie
   * am Foto im Tagebuch. Ein „Übernehmen"-Knopf darunter wäre ein zweiter
   * Klick für eine Wahl, die verlustfrei änderbar ist.
   *
   * ## Die Bilder werden nacheinander geholt
   *
   * Dasselbe Muster und derselbe Grund wie in der Reiseliste
   * (`titelbilderNachladen`): Eine Reise mit vierzig Fotos schickte sonst
   * vierzig gleichzeitige Anfragen nach Bilddaten los. Nacheinander ist nicht
   * langsamer, aber es nimmt dem Server nichts weg — die Kacheln füllen sich
   * von oben nach unten, und wer sein Bild schon sieht, klickt es an, während
   * der Rest noch lädt.
   *
   * Jede Objekt-URL wird beim Schließen wieder freigegeben. Der Aufräumer liest
   * dafür **nicht** die Anzeige-Map, sondern eine schlichte Liste daneben:
   * Sonst liefe der Effekt bei jedem neuen Bild noch einmal an und gäbe frei,
   * was gerade dasteht. Wortgleich zur Reiseliste, und dort steht die lange
   * Fassung dieser Begründung.
   *
   * ## Seit dem 20.9.2026: derselbe Dialog für ein Kapitel
   *
   * Auf der geteilten Seite trägt nicht nur die Reise ein Bild, sondern jede
   * Gegend. Auch dort rechnete bisher nur eine Automatik, und auch dort fehlte
   * der Griff. Der Dialog dafür ist **dieser** und keine Kopie: Es ist
   * dieselbe Handlung an derselben Sammlung von Fotos, und zwei Flächen dafür
   * liefen auseinander — eine zeigte eines Tages andere Bilder als die andere,
   * und niemand könnte sagen, welche recht hat.
   *
   * Parametrisiert ist genau das, was sich unterscheidet: die Überschrift, der
   * Erklärsatz darunter, und welche Fotos zuerst stehen (`bevorzugt` — die
   * Einträge dieser Gegend). Alles andere ist wortgleich, einschließlich der
   * Marke an ungeteilten Fotos: Die geteilte Seite zeigt sie weder als
   * Titelbild noch als Kapitelbild, und die Regel ist dieselbe
   * (`oeffentlich.gewaehltes_bild`).
   */
  import { api } from './api';
  import type { Tagebucheintrag } from './tagebuch';
  import { istGewaehlt, type Titelbildwahl } from './titelbild';
  import {
    NICHT_GETEILT,
    hatFotos,
    kandidaten,
    ungeteilte,
    type Bildkandidat,
    type Gegendstationen
  } from './titelbildwahl';
  import Hinweis from '../gestaltung/muster/Hinweis.svelte';
  import Ladezustand from '../gestaltung/muster/Ladezustand.svelte';
  import { fokusfalle } from '../gestaltung/fokusfalle';

  interface Props {
    /** Die Reise, deren Fotos zur Wahl stehen. */
    tripId: number;
    /** Ihr Name — er steht im Titel, damit man die richtige Reise erwischt. */
    reise: string;
    /**
     * Die **ausdrückliche** Wahl aus `settings` — `null` heißt: Automatik.
     *
     * Nicht das Bild, das die Liste gerade zeigt: Bei laufender Automatik ist
     * eines von beiden zufällig dasselbe, und ein Haken daran läse sich als
     * „ausgesucht". Dieselbe Unterscheidung wie bei `istGewaehlt`.
     */
    wahl?: Titelbildwahl | null;
    /**
     * Für welche Gegend gewählt wird — `null` heißt: für die ganze Reise.
     *
     * Der Name und nicht eine Nummer: Er steht in der Überschrift, und er ist
     * zugleich der Schlüssel, unter dem die Wahl abgelegt wird
     * (`titelbild.mitKapitelbild`).
     */
    kapitel?: string | null;
    /**
     * Die **Stationen** dieser Gegend — ihre Fotos stehen im Raster zuerst —
     * und die Reisereihenfolge aller Stationen dazu.
     *
     * Der Aufrufer rechnet beides aus (`kapitel.kapitelVon` über die
     * Stationen, die er ohnehin hält). Über die Stationen und nicht über die
     * Einträge, weil dieser Dialog die Einträge selbst holt; die Reihenfolge
     * braucht die Heimatregel für Einträge ohne Ortsangabe — siehe
     * `titelbildwahl.kandidaten`. Ohne Angabe bleibt die Reihenfolge die alte:
     * geteilt zuerst, darin neueste zuerst.
     */
    bevorzugt?: Gegendstationen | null;
    onwaehlen: (wahl: Titelbildwahl | null) => void;
    onabbrechen?: () => void;
  }
  let {
    tripId,
    reise,
    wahl = null,
    kapitel = null,
    bevorzugt = null,
    onwaehlen,
    onabbrechen
  }: Props = $props();

  /**
   * „Kapitelbild" gegen „Titelbild" — das Wort für Knopftitel und Vorlesegerät.
   *
   * Deutsche Anführungszeichen stehen hier bewusst nicht im Skript, sondern nur
   * im Markup darunter: Das schließende wird beim Schreiben regelmäßig als
   * gerades ASCII gesetzt und beendet dann die Zeichenkette (Hausregel).
   */
  const rolle = $derived(kapitel ? 'Kapitelbild' : 'Titelbild');

  let eintraege = $state<Tagebucheintrag[] | null>(null);
  let fehler = $state<string | null>(null);

  const liste = $derived(eintraege ? kandidaten(eintraege, bevorzugt) : []);
  const leer = $derived(eintraege != null && !hatFotos(eintraege));
  const mitPrivaten = $derived(ungeteilte(liste) > 0);

  /** Objekt-URL je Medium — der Schlüssel ist die Medienkennung, nicht der Platz. */
  let bilder = $state(new Map<number, string>());
  /** Zum Freigeben beim Schließen. Siehe der Kopfkommentar. */
  const objektUrls: string[] = [];
  $effect(() => () => {
    for (const url of objektUrls) URL.revokeObjectURL(url);
  });

  /** Der Dialog ist zu — dann hört das Nachladen auf, wo es gerade ist. */
  let beendet = false;
  $effect(() => () => {
    beendet = true;
  });

  laden();

  async function laden() {
    let roh: Tagebucheintrag[];
    try {
      // Derselbe Weg, den das Tagebuch ohnehin geht: Einträge samt Medien in
      // einer Anfrage. Ein eigener Endpunkt „nur die Fotos" wäre eine zweite
      // Stelle, an der die Rechteprüfung stimmen müsste.
      roh = await api.tagebuch(tripId);
    } catch (e) {
      fehler = e instanceof Error ? e.message : 'Die Fotos ließen sich nicht laden.';
      return;
    }
    eintraege = roh;
    // Aus der frisch geholten Liste und nicht aus `liste`: Dieselbe Rechnung,
    // aber ohne die Frage, wann ein abgeleiteter Wert nach einem `await`
    // schon nachgezogen ist.
    await vorschauenNachladen(kandidaten(roh, bevorzugt));
  }

  /**
   * Die Vorschauen holen — eine nach der anderen, in der Reihenfolge des
   * Rasters.
   *
   * **Fehler sind still.** Ein einzelnes Foto, das nicht kommt, hinterlässt
   * eine leere Kachel; ein roter Balken über der ganzen Wahl wäre die falsche
   * Größenordnung. Über die Vorschau und nicht über das Original: Eine Kachel
   * ist 100 px hoch, und vierzig Handyfotos in Originalgröße wären ein paar
   * hundert Megabyte für eine Auswahl.
   */
  async function vorschauenNachladen(reihe: Bildkandidat[]) {
    for (const k of reihe) {
      if (beendet) return;
      try {
        const url = await api.medienHolen(tripId, k.eintrag, k.medium, true);
        objektUrls.push(url);
        // Neue `Map` statt `set` am Ort: Svelte sieht die Änderung sonst nicht.
        bilder = new Map(bilder).set(k.medium, url);
      } catch {
        // Still — siehe oben.
      }
    }
  }

  /** Der Alternativtext einer Kachel: die Beschriftung, sonst die Ordnungszahl. */
  function alt(k: Bildkandidat, nr: number): string {
    return k.beschriftung ?? `Foto ${nr}`;
  }
</script>

<svelte:window
  onkeydown={(e) => {
    if (e.key === 'Escape') onabbrechen?.();
  }}
/>

<!-- Die Rolle sitzt am Rahmen und nicht am Raster — dieselbe Aufteilung wie in
     `Kopierwahl.svelte`, samt Begründung. -->
<div class="titelbildwahl" role="dialog" aria-modal="true" aria-label={rolle + ' wählen'} use:fokusfalle>
  <div class="oben">
    {#if kapitel}
      <h2>Bild für „{kapitel}“</h2>
      <!-- Der Satz sagt beides: wo das Bild steht und was ohne Wahl passiert.
           Die Reihenfolge des Rasters wird ausdrücklich genannt — sonst sieht
           es so aus, als fehlten die übrigen Fotos der Reise. -->
      <p class="wozu">
        Das Bild steht über dem Kapitel auf der <strong>geteilten Seite</strong>.
        Ohne Wahl sucht die Automatik aus: das jüngste Foto dieser Gegend. Die
        Fotos aus <em>{kapitel}</em> stehen zuerst, darunter die übrigen der Reise.
      </p>
    {:else}
      <h2>Titelbild für „{reise}“</h2>
      <p class="wozu">
        Das Bild steht über der Reise in <em>Meine Reisen</em>. Ohne Wahl sucht die
        Automatik aus: das jüngste Foto aus einem geteilten Tagebucheintrag.
      </p>
    {/if}
    <!-- Nur, wenn es ungeteilte Fotos gibt. Ein Satz über einen Unterschied,
         den diese Reise nicht hat, entwertet den nächsten Satz. -->
    {#if mitPrivaten}
      <p class="wozu">
        {#if kapitel}
          <!-- Erste Hälfte anders, zweite wortgleich: Ein Kapitelbild steht
               heute ausschließlich auf der geteilten Seite, und dort gilt die
               harte Regel. Der Satz aus der Titelbildwahl („sie stehen dann in
               deiner Übersicht") wäre hier eine Zusage auf eine Fläche, die es
               nicht gibt. -->
          Fotos aus zurückgehaltenen Einträgen stehen hier zur Wahl, weil es deine
          Fotos sind.
        {:else}
          Fotos aus zurückgehaltenen Einträgen darfst du wählen — sie stehen dann in
          deiner Übersicht.
        {/if}
        Auf der <strong>geteilten Seite</strong> erscheinen sie nie; dort gilt
        weiterhin das automatisch gewählte Bild.
      </p>
    {/if}
  </div>

  {#if fehler}
    <Hinweis art="ernst" titel="Fotos nicht abrufbar">
      {fehler} Deine Bilder sind nicht verloren — sie liegen dort und sind gerade nur
      nicht zu erreichen.
    </Hinweis>
  {:else if eintraege == null}
    <Ladezustand zeilen={2} text="holt die Fotos dieser Reise …" />
  {:else if leer}
    <!-- Kein `Leerzustand` mit Illustration: Der ist für eine Fläche gedacht,
         die man betritt und wieder verlässt. Hier steht der Satz zwischen
         Überschrift und Knöpfen, und mehr ist nicht zu sagen. -->
    <p class="nichts">
      In dieser Reise liegt noch kein Foto. Sobald du im Tagebuch eines anhängst,
      steht es hier zur Wahl — und wenn der Eintrag geteilt ist, nimmt die
      Automatik es von selbst.
    </p>
  {:else}
    <ul class="raster">
      {#each liste as k, i (k.medium)}
        {@const bild = bilder.get(k.medium) ?? null}
        {@const gewaehlt = istGewaehlt(wahl, k.eintrag, k.medium)}
        <li>
          <button
            type="button"
            class="kachel"
            class:gewaehlt
            aria-pressed={gewaehlt}
            title={gewaehlt
              ? `${alt(k, i + 1)} — das gewählte ${rolle}`
              : `${alt(k, i + 1)} als ${rolle} setzen`}
            onclick={() => onwaehlen({ eintrag: k.eintrag, medium: k.medium })}
          >
            {#if bild}
              <img
                src={bild}
                alt={alt(k, i + 1)}
                width={k.breite ?? undefined}
                height={k.hoehe ?? undefined}
              />
            {:else}
              <!-- Ein ruhiges Feld, solange geladen wird. Kein Kreisel je
                   Kachel: Vierzig davon nebeneinander sind ein Flimmern, und
                   das Raster steht ohnehin schon in seiner endgültigen Form. -->
              <span class="platzhalter" aria-hidden="true"></span>
            {/if}
            {#if gewaehlt}
              <span class="marke">gewählt</span>
            {/if}
            {#if !k.geteilt}
              <!-- An der Kachel und nicht nur im Satz oben: Wer scrollt, liest
                   die Erklärung nicht noch einmal. Auf der Kachel steht die
                   kurze Fassung (mehr passt bei 9 rem nicht), der ganze Satz
                   im Titel — er ist derselbe wie in `titelbildwahl.ts`, damit
                   die Zusage an einer Stelle steht. -->
              <span class="privat" title={NICHT_GETEILT}>nicht geteilt</span>
            {/if}
          </button>
        </li>
      {/each}
    </ul>
  {/if}

  <div class="knoepfe">
    <!-- Der Stand vor der Wahl, ganz links und ganz leise: Ohne ihn ist nicht
         zu erkennen, ob gerade eine Wahl gilt oder die Automatik läuft — und
         genau das entscheidet, ob der Knopf daneben etwas zu tun hat. -->
    {#if !wahl && !leer && !fehler}
      <span class="lauft">Zurzeit sucht die Automatik aus.</span>
    {/if}
    <!-- Abbrechen links von der zweiten Handlung und mit dem Anfangsfokus:
         derselbe Grund wie an der Kopier-Abfrage — der Weg heraus darf nicht
         gesucht werden müssen. -->
    <!-- svelte-ignore a11y_autofocus -->
    <button type="button" autofocus onclick={onabbrechen}>Abbrechen</button>
    {#if wahl}
      <!-- Nur bei einer ausdrücklichen Wahl. Ohne sie läuft die Automatik
           bereits, und ein Knopf, der nichts ändert, ist ein Versprechen auf
           eine Wirkung, die es nicht gibt. -->
      <button type="button" class="still" onclick={() => onwaehlen(null)}>
        Automatik wiederherstellen
      </button>
    {/if}
  </div>
</div>

<style>
  .titelbildwahl {
    max-width: 42rem;
    width: 100%;
    background: var(--surface-hoch);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--schatten);
    padding: var(--raum-8);
    display: grid;
    gap: var(--gruppe-eng);
  }

  .oben {
    display: grid;
    gap: var(--raum-3);
  }

  h2 {
    margin: 0;
    font-size: var(--fs-titel);
    font-weight: var(--gw-halb);
  }

  .wozu {
    margin: 0;
    font-size: var(--fs-fein);
    color: var(--text-dim);
    text-wrap: pretty;
  }

  .nichts {
    margin: 0;
    font-size: var(--fs-text);
    color: var(--text-dim);
    text-wrap: pretty;
  }

  /* Feste Spaltenbreite statt `auto-fill` über die ganze Fläche: Die Kacheln
     sollen bei drei Fotos genauso groß sein wie bei vierzig — ein Raster, das
     seine Zellen nach der Anzahl streckt, zeigt dasselbe Bild in jeder Reise
     anders groß. Auf dem Telefon werden es von selbst zwei Spalten. */
  .raster {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
    gap: var(--raum-3);
    /* Vierzig Fotos sind länger als jedes Fenster; der Dialog selbst soll
       dabei stehen bleiben und nur das Raster rollen. */
    max-height: 46vh;
    overflow-y: auto;
  }

  .kachel {
    position: relative;
    display: block;
    width: 100%;
    /* Alle Kacheln gleich hoch, quer wie hoch aufgenommen: Ein Raster aus
       verschieden hohen Bildern ist beim Suchen unruhiger als eines, das
       beschneidet. Das Original bleibt unangetastet — beschnitten wird die
       Anzeige. */
    aspect-ratio: 4 / 3;
    padding: 0;
    border: 2px solid transparent;
    border-radius: var(--radius-mini);
    background: var(--surface-2);
    overflow: hidden;
    cursor: pointer;
  }
  .kachel img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .platzhalter {
    display: block;
    width: 100%;
    height: 100%;
    background: var(--surface-2);
  }
  .kachel:hover {
    border-color: var(--border-stark);
  }
  .kachel:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  /* Der Rahmen trägt die Auskunft, nicht die Marke allein: Auf einem hellen
     Foto ist ein weißes Etikett schlecht zu sehen, ein Rahmen um die ganze
     Kachel immer. */
  .kachel.gewaehlt {
    border-color: var(--accent);
  }

  /* Beide Etiketten auf demselben dunklen Grund und in Weiß — dieselbe
     Überlegung wie beim Schleier des Kapitelkopfs: Ein Foto ist in beiden
     Themen dasselbe Foto, und Weiß auf Dunkel trägt darüber immer. */
  .marke,
  .privat {
    position: absolute;
    left: 4px;
    font-size: var(--fs-mikro);
    line-height: 1.4;
    padding: 1px 6px;
    border-radius: var(--radius-rund);
    color: #fff;
    background: rgb(0 0 0 / 0.62);
  }
  .marke {
    top: 4px;
    background: var(--accent);
    color: var(--text-auf-akzent);
    font-weight: var(--gw-halb);
  }
  .privat {
    bottom: 4px;
    right: 4px;
    left: auto;
  }

  .knoepfe {
    display: flex;
    align-items: center;
    gap: var(--raum-5);
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .knoepfe button {
    font: inherit;
    font-size: var(--fs-basis);
    padding: 8px 17px;
    border-radius: var(--radius);
    cursor: pointer;
  }
  /* Zurückhaltend, weil es die seltenere Absicht ist: Wer den Dialog öffnet,
     will meistens ein Bild aussuchen und nicht die Wahl aufgeben. */
  .knoepfe button.still {
    border: none;
    background: none;
    color: var(--text-dim);
    text-decoration: underline;
  }
  .lauft {
    font-size: var(--fs-fein);
    color: var(--text-schwach);
    margin-right: auto;
  }
</style>
